import aiGenerateApi from './aiGenerate'

const FAQ_SYSTEM_PROMPT = `
You are a GEO-optimised travel content writer for Lago di Garda Camping.

Your task is to generate FAQ blocks designed to rank in AI-powered search engines (ChatGPT, Claude, Gemini) by directly answering the questions real travelers ask before booking.

CRITICAL: Respond ONLY with a valid JSON object. No markdown, no code fences, no explanations.

Output schema (property names MUST be exactly "faqs", "question", and "answer" — ASCII keys only):
{
  "faqs": [
    {
      "question": "string",
      "answer": "string"
    }
  ]
}

Requirements:
- Ground every FAQ strictly in the page title and page body provided. Do not invent or infer facts not present in the source content.
- Write questions in the natural conversational form a traveler would use when asking an AI assistant.
- The first sentence of each answer must directly and completely address the question — no preamble.
- Each answer must be 2–3 sentences maximum. Concise, factual, user-facing.
- Return 4 to 6 FAQs.
- Output language must match the target language specified in the user message.
`

const LANGUAGE_LABELS = {
  italiano: 'Italian',
  inglese: 'English',
  tedesco: 'German',
  olandese: 'Dutch',
  danese: 'Danish',
  polacco: 'Polish',
}

function buildFaqPrompt(language, currentFaqList, pageTitle, pageDescription) {
  const langLabel = LANGUAGE_LABELS[language] || LANGUAGE_LABELS.italiano
  const titleBlock = (pageTitle || '').trim() || '(empty)'
  const bodyBlock = (pageDescription || '').trim() || '(empty)'
  return `Generate a complete FAQ block for a tourism/camping page.

Target language: ${langLabel}

Page title (this language):
${titleBlock}

Page body / description (plain text from the editor for this language):
${bodyBlock}

Current FAQ content for this language (optional context):
${JSON.stringify(currentFaqList || [], null, 2)}
`
}

function extractFaqsArray(data) {
  if (!data || typeof data !== 'object') return null
  const raw = data.faqs ?? data.faq ?? data.FAQs ?? data.FAQ
  return Array.isArray(raw) ? raw : null
}

function normalizeFaqRow(raw) {
  const question =
    (typeof raw?.question === 'string' && raw.question) ||
    (typeof raw?.domanda === 'string' && raw.domanda) ||
    (typeof raw?.q === 'string' && raw.q) ||
    ''
  const answer =
    (typeof raw?.answer === 'string' && raw.answer) ||
    (typeof raw?.risposta === 'string' && raw.risposta) ||
    (typeof raw?.a === 'string' && raw.a) ||
    ''
  return {
    question: question.trim(),
    answer: answer.trim(),
  }
}

const generateFaq = async (language, currentFaqList, { model, pageTitle = '', pageDescription = '' } = {}) => {
  const response = await aiGenerateApi.generateStructuredText({
    prompt: buildFaqPrompt(language, currentFaqList, pageTitle, pageDescription),
    systemPrompt: FAQ_SYSTEM_PROMPT,
    model: model || 'gpt-4o-mini',
  })
  if (!response.ok || !response.data) return response

  const rawList = extractFaqsArray(response.data)
  if (!rawList || rawList.length === 0) {
    return { ok: false, data: { error: 'Invalid response from generation service.' } }
  }

  const normalized = rawList.map(normalizeFaqRow).filter((faq) => faq.question.length > 0 && faq.answer.length > 0)

  if (normalized.length === 0) {
    return { ok: false, data: { error: 'Invalid response from generation service.' } }
  }

  return { ok: true, data: { faqs: normalized } }
}

export default { generateFaq }
